import type { MockDatabase } from "@/types/domain";
import type { MockRequest, MockResponse } from "@/mocks/router";
import { extras, store } from "@/mocks/store";
import { err, matchPath, nowStamp, ok, paginate, pushAudit } from "./_utils";

export function handleShareConfigs(req: MockRequest): MockResponse | null {
  const method = req.method.toUpperCase();
  const path = req.path.replace(/\/+$/, "") || "/";
  const { query, body } = req;

  if (method === "GET" && path === "/api/share-cards") return ok(paginate(store.SHARE_CONFIGS, query));
  {
    const m = matchPath("/api/share-cards/:id", path);
    if (m && method === "PATCH") {
      const idx = store.SHARE_CONFIGS.findIndex((x) => x.id === m.id || x.config_id === m.id);
      if (idx < 0) return err(404, "NOT_FOUND", "分享卡片不存在");
      store.SHARE_CONFIGS[idx] = { ...store.SHARE_CONFIGS[idx], ...(body as object), updated: nowStamp() };
      return ok({ data: store.SHARE_CONFIGS[idx] });
    }
  }
  if (method === "GET" && path === "/api/share-cards/special") return ok({ data: extras.specialCards, meta: { total: extras.specialCards.length, page: 1, pageSize: 50 } });
  if (method === "POST" && path === "/api/share-cards/special") {
    const item = { id: `spc_${Date.now().toString(36)}`, ...(body as object), created_at: nowStamp() };
    extras.specialCards.push(item);
    return ok({ data: item }, 201);
  }

  if (method === "GET" && path === "/api/platform/share-languages") return ok({ data: extras.shareLanguages });
  if (method === "PUT" && path === "/api/platform/share-languages") {
    extras.shareLanguages = body;
    return ok({ data: extras.shareLanguages });
  }
  if (method === "GET" && path === "/api/platform/regions-countries") return ok({ data: extras.regionsCountries });
  if (method === "PUT" && path === "/api/platform/regions-countries") {
    extras.regionsCountries = body;
    return ok({ data: extras.regionsCountries });
  }

  if (method === "GET" && path === "/api/share-configs") {
    let items = [...store.SHARE_CONFIGS];
    const scope = query.get("scope");
    const status = query.get("status");
    if (scope) items = items.filter((c) => c.scope === scope);
    if (status) items = items.filter((c) => c.status === status);
    return ok(paginate(items, query));
  }
  if (method === "POST" && path === "/api/share-configs") {
    const payload = body as Partial<MockDatabase["SHARE_CONFIGS"][number]>;
    const id = payload.id || payload.config_id || `cfg_${Date.now().toString(36)}`;
    const cfg = {
      id,
      config_id: id,
      landing_page_ids: payload.landing_page_ids || [],
      landing_page_id: payload.landing_page_id || payload.landing_page_ids?.[0] || "",
      temple_id: payload.temple_id ?? null,
      name: payload.name || "未命名配置",
      scope: payload.scope || "package_set",
      mode: payload.mode || "quick",
      package_ids: payload.package_ids || [],
      landing_rules: payload.landing_rules || [],
      status: payload.status || "draft",
      channels: payload.channels || [],
      updated: nowStamp(),
      owner: payload.owner || store.ROLES[store.SESSION.currentRole]?.user || "unknown",
      title: payload.title || "",
      desc: payload.desc,
      url: payload.url,
      share_url: payload.share_url,
      image_url: payload.image_url,
      share_copy: payload.share_copy,
      scenario_ids: payload.scenario_ids,
    };
    store.SHARE_CONFIGS.push(cfg);
    pushAudit("创建分享配置", cfg.name, cfg.id);
    return ok({ data: cfg }, 201);
  }
  {
    const m = matchPath("/api/share-configs/:id/activity-logs", path);
    if (m && method === "GET") {
      const logs = extras.activityLogs[m.id] || [];
      return ok({ data: logs, meta: { total: logs.length, page: 1, pageSize: 50 } });
    }
    if (m && method === "POST") {
      extras.activityLogs[m.id] = extras.activityLogs[m.id] || [];
      const log = { id: `log_${Date.now()}`, at: nowStamp(), ...(body as object) };
      extras.activityLogs[m.id].unshift(log);
      return ok({ data: log }, 201);
    }
  }
  {
    const m = matchPath("/api/share-configs/:id", path);
    if (m && method === "GET") {
      const item = store.SHARE_CONFIGS.find((x) => x.id === m.id || x.config_id === m.id);
      if (!item) return err(404, "NOT_FOUND", "配置不存在");
      return ok({ data: item });
    }
    if (m && method === "PATCH") {
      const idx = store.SHARE_CONFIGS.findIndex((x) => x.id === m.id || x.config_id === m.id);
      if (idx < 0) return err(404, "NOT_FOUND", "配置不存在");
      store.SHARE_CONFIGS[idx] = { ...store.SHARE_CONFIGS[idx], ...(body as object), updated: nowStamp() };
      extras.activityLogs[m.id] = extras.activityLogs[m.id] || [];
      extras.activityLogs[m.id].unshift({ id: `log_${Date.now()}`, at: nowStamp(), action: "update", detail: body });
      return ok({ data: store.SHARE_CONFIGS[idx] });
    }
    if (m && method === "DELETE") {
      const idx = store.SHARE_CONFIGS.findIndex((x) => x.id === m.id || x.config_id === m.id);
      if (idx < 0) return err(404, "NOT_FOUND", "配置不存在");
      const [removed] = store.SHARE_CONFIGS.splice(idx, 1);
      return ok({ data: removed });
    }
  }

  if (method === "GET" && path === "/api/experiments") return ok(paginate(extras.experiments as unknown[], query));
  if (method === "POST" && path === "/api/experiments") {
    const item = { id: `exp_${Date.now().toString(36)}`, status: "draft", created_at: nowStamp(), ...(body as object) };
    extras.experiments.push(item);
    return ok({ data: item }, 201);
  }
  {
    const m = matchPath("/api/experiments/:id/analysis", path);
    if (m && method === "GET") return ok({ data: { experimentId: m.id, metric: "conversion", rows: store.CHANNEL_COMPARE.slice(0, 3) } });
  }
  {
    const m = matchPath("/api/experiments/:id/retention", path);
    if (m && method === "GET") return ok({ data: { experimentId: m.id, metric: "retention", rows: store.CHANNEL_COMPARE.map((c) => ({ channel: c.code, ...c.retention })) } });
  }
  {
    const m = matchPath("/api/experiments/:id", path);
    if (m && method === "PATCH") {
      const idx = extras.experiments.findIndex((x) => String(x.id) === m.id);
      if (idx < 0) return err(404, "NOT_FOUND", "实验不存在");
      extras.experiments[idx] = { ...extras.experiments[idx], ...(body as object), updated_at: nowStamp() };
      return ok({ data: extras.experiments[idx] });
    }
  }

  if (method === "GET" && path === "/api/scenarios") return ok({ data: store.SHARE_SCENARIOS || [] });

  return null;
}
